import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * The server-rendered market update, in a frame so its email styles cannot
 * leak into the CRM (or the CRM's into it). `allow-same-origin` without
 * `allow-scripts` lets us read its height to size the frame while still never
 * running anything inside it. Links open in a new tab.
 */
const MarketUpdateEmailFrame = ({ html, title, width = "100%", minHeight = 480, className = "" }) => {
  const ref = useRef(null);
  const [height, setHeight] = useState(minHeight);

  const measure = useCallback(() => {
    const doc = ref.current?.contentDocument;
    if (!doc?.body) return;
    doc.querySelectorAll("a[href]").forEach((a) => {
      const href = a.getAttribute("href") || "";
      if (!href.startsWith("mailto:") && !href.startsWith("tel:")) {
        a.setAttribute("target", "_blank");
        a.setAttribute("rel", "noopener noreferrer");
      }
    });
    setHeight(Math.max(minHeight, doc.documentElement.scrollHeight));
  }, [minHeight]);

  useEffect(() => {
    // Images load after the document, and each one changes the height.
    const doc = ref.current?.contentDocument;
    if (!doc) return undefined;
    const imgs = Array.from(doc.images || []);
    imgs.forEach((img) => img.addEventListener("load", measure));
    return () => imgs.forEach((img) => img.removeEventListener("load", measure));
  }, [html, width, measure]);

  useEffect(() => { measure(); }, [width, measure]);

  return (
    <iframe
      ref={ref}
      title={title}
      srcDoc={html}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      onLoad={measure}
      className={`block mx-auto border-0 bg-white transition-[width] duration-200 ease-out ${className}`}
      style={{ width, height }}
    />
  );
};

export default MarketUpdateEmailFrame;
